import { Text, Spacer } from "@hackthenorth/north";
import { GraphQLError } from "graphql";
import { Button, Link, Select, TextInput } from "north.js";
import React, { useState, useEffect } from "react";
import { styled } from "twin.macro";

import {
  Divider,
  Dropzone,
  errorToast,
  FileLink,
  Icon,
  IMAGE_FILE_TYPES,
  successToast,
} from "src/shared/components";
import Label from "src/shared/components/Label";
import { useHackerContext } from "src/shared/contexts";
import { Field } from "src/shared/contexts/HackerContext/types";
import { useHackerState } from "src/shared/contexts/HackerContext/useHackerState";
import { formatDateForSubmit } from "src/shared/utils/date";
import { optionsToNorthV2Options } from "src/shared/utils/react-select";
import { notBlank, isNonNegative } from "src/shared/utils/validation";
import { formatValidationIssues } from "src/shared/utils/validationMessages";

import { useFileDropzone } from "../career/useFileDropzone";
import { getReimbursementAmount } from "../travel/ReimbursementAmount";

import { BusComponent } from "./busComponent";
import {
  REIMBURSEMENT_SELECT_OPTIONS,
  ACCEPTED_FIELDS,
  VALIDATORS as BASE_VALIDATORS,
} from "./constants";
import { FlightDetails } from "./flightDetails";
import { Reimbursement } from "./reimbursement";
import TravelModal from "./travelModal";
import { isRapidContactInformation, validateAll } from "./validation";

interface FlightFormProps {
  isFlightClosed: boolean;
  isReadOnly: boolean;
}

export const FlightForm: React.FC<FlightFormProps> = ({
  isFlightClosed,
  isReadOnly,
}) => {
  const { updateResponses, navigateNext, claimData } = useHackerContext();
  const { responsesState, setResponsesState, isValid } = useHackerState(
    ACCEPTED_FIELDS,
    BASE_VALIDATORS
  );

  const [isBlocking, setIsBlocking] = useState(false);
  const [receiptError, setReceiptError] = useState("");

  const travelLocation =
    (claimData[Field.SHORT_LOCATION] as string | null) ??
    (claimData[Field.LOCATION] as string | null);
  const reimbursementAmount = getReimbursementAmount(travelLocation).amount;

  const isRapidContactInformationValid = isRapidContactInformation(
    responsesState[Field.TRAVEL_PHONE_NUMBER]
  );

  const isVisaTrackingValid = responsesState[Field.REQUIRES_VISA]
    ? notBlank(responsesState[Field.VISA_TRACKING])
    : true;

  const isFlightAmountValid =
    notBlank(responsesState[Field.FLIGHT_AMOUNT]) &&
    isNonNegative(responsesState[Field.FLIGHT_AMOUNT]);

  const reimbursementMethod = responsesState[Field.REIMBURSEMENT_METHOD] as
    | string
    | null;
  const isReimbursementRecipientValid =
    reimbursementMethod === "paypal"
      ? notBlank(responsesState[Field.REIMBURSEMENT_RECIPIENT_PAYPAL])
      : reimbursementMethod === "cadETransfer"
      ? notBlank(responsesState[Field.REIMBURSEMENT_RECIPIENT_ETRANSFER])
      : true;

  const VALIDATORS = {
    ...BASE_VALIDATORS,
    [Field.TRAVEL_PHONE_NUMBER]: isRapidContactInformation,
    [Field.VISA_TRACKING]: responsesState[Field.REQUIRES_VISA]
      ? notBlank
      : null,
    [Field.FLIGHT_AMOUNT]: (value: unknown) =>
      notBlank(value) && isNonNegative(value),
    [Field.REIMBURSEMENT_RECIPIENT_PAYPAL]: notBlank,
    [Field.REIMBURSEMENT_RECIPIENT_ETRANSFER]: notBlank,
    [Field.REIMBURSEMENT_RECIPIENT_NAME]: notBlank,
    [Field.ARRIVAL_AIRPORT]: notBlank,
    [Field.FRIDAY_FLIGHT_NUMBER]: notBlank,
    [Field.FRIDAY_FLIGHT_AIRLINE]: notBlank,
    [Field.FRIDAY_FLIGHT_ARRIVAL_TIME]: notBlank,
    [Field.DEPARTURE_AIRPORT]: notBlank,
    [Field.SUNDAY_FLIGHT_NUMBER]: notBlank,
    [Field.SUNDAY_FLIGHT_AIRLINE]: notBlank,
  };

  const [showError, setShowError] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({
    [Field.TRAVEL_PHONE_NUMBER]: "",
    [Field.FLIGHT_CURRENCY]: "",
    [Field.FLIGHT_AMOUNT]: "",
    [Field.VISA_TRACKING]: "",
    [Field.FRIDAY_FLIGHT_AIRLINE]: "",
    [Field.ARRIVAL_AIRPORT]: "",
    [Field.FRIDAY_FLIGHT_NUMBER]: "",
    [Field.FRIDAY_FLIGHT_ARRIVAL_TIME]: "",
    [Field.SUNDAY_FLIGHT_AIRLINE]: "",
    [Field.DEPARTURE_AIRPORT]: "",
    [Field.SUNDAY_FLIGHT_NUMBER]: "",
    [Field.SUNDAY_FLIGHT_DEPARTURE_TIME]: "",
    [Field.REIMBURSEMENT_METHOD]: "",
    followup: "",
    [Field.REIMBURSEMENT_ACKNOWLEDGEMENT]: "",
  });
  const [didEdit, setDidEdit] = useState({
    currency: false,
    amount: false,
    visaTracking: false,
  });

  const ACCEPTED_FILE_TYPES = IMAGE_FILE_TYPES;

  const {
    dropzoneInputRef,
    fetchedFile,
    loading: isFileLoading,
    error: fileError,
    addFile,
    deleteFile,
    onFileError,
    setDropzoneErrorMessage,
  } = useFileDropzone(
    responsesState[Field.TRAVEL_RECEIPT]?.[0],
    (files) => {
      setResponsesState(Field.TRAVEL_RECEIPT, files);
    },
    ACCEPTED_FILE_TYPES
  );

  const ReceiptUpload = () => (
    <div>
      {!isReadOnly && (
        <Dropzone
          min={1}
          max={1}
          label={
            <span>
              <b>Upload a file</b> or drag and drop
            </span>
          }
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          onFilesUpload={(files) => {
            // Clear any prior "missing receipt" error when a file is selected
            if (files?.length) setReceiptError("");
            addFile(files);
          }}
          onError={(err) => {
            onFileError(err);
            // Preserve the generic receipt error only when no file is present
            if (fetchedFile) setReceiptError("");
          }}
          ref={dropzoneInputRef}
          onBlur={() => setDropzoneErrorMessage("")}
        />
      )}
      {fetchedFile && (
        <>
          <Spacer height={8} />
          <FileLink
            file={fetchedFile}
            deleteFile={
              isReadOnly
                ? undefined
                : () => {
                    // If the file is removed, surface the receipt requirement again
                    deleteFile();
                    setReceiptError("Please upload your flight receipt.");
                  }
            }
          />
        </>
      )}
      {isReadOnly && !fetchedFile && <Text>No receipt was uploaded.</Text>}
      {receiptError && <ErrorText>{receiptError}</ErrorText>}
      {fileError && <ErrorText>{fileError}</ErrorText>}
    </div>
  );

  const updateErrors = (response: any) => {
    setShowError(true);
    const newErrors = validateAll(response);
    setErrors({ ...errors, ...newErrors });
  };

  const convertResponseTypes = (responses: object) => {
    const fridayString = responses[Field.FRIDAY_FLIGHT_ARRIVAL_TIME] as string;
    const fridayVal = formatDateForSubmit(fridayString);

    const sundayString = responses[
      Field.SUNDAY_FLIGHT_DEPARTURE_TIME
    ] as string;
    const sundayVal = formatDateForSubmit(sundayString);

    return {
      ...responses,
      [Field.FRIDAY_FLIGHT_ARRIVAL_TIME]: fridayVal,
      [Field.SUNDAY_FLIGHT_DEPARTURE_TIME]: sundayVal,
      [Field.FLIGHT_AMOUNT]: parseFloat(responses[Field.FLIGHT_AMOUNT]),
    };
  };

  const customValid = () => {
    let valid = isValid;
    if (responsesState[Field.REQUIRES_VISA]) {
      const validator = VALIDATORS[Field.VISA_TRACKING];
      if (validator) {
        valid = valid && validator(responsesState[Field.VISA_TRACKING]);
      }
    }

    const flightAmountValidator = VALIDATORS[Field.FLIGHT_AMOUNT];
    if (flightAmountValidator) {
      valid =
        valid && flightAmountValidator(responsesState[Field.FLIGHT_AMOUNT]);
    }

    valid = valid && isReimbursementRecipientValid;

    return valid;
  };

  const getValidationIssues = () => {
    const fieldIsValid = (field: Field) => {
      const validator = BASE_VALIDATORS[field];
      return validator ? validator(responsesState[field]) : true;
    };
    return [
      {
        label: "Friday arrival airport",
        isValid: fieldIsValid(Field.ARRIVAL_AIRPORT),
      },
      {
        label: "Friday flight number",
        isValid: fieldIsValid(Field.FRIDAY_FLIGHT_NUMBER),
      },
      {
        label: "Friday airline",
        isValid: fieldIsValid(Field.FRIDAY_FLIGHT_AIRLINE),
      },
      {
        label: "Friday arrival date and time",
        isValid: fieldIsValid(Field.FRIDAY_FLIGHT_ARRIVAL_TIME),
      },
      {
        label: "Sunday departure airport",
        isValid: fieldIsValid(Field.DEPARTURE_AIRPORT),
      },
      {
        label: "Sunday flight number",
        isValid: fieldIsValid(Field.SUNDAY_FLIGHT_NUMBER),
      },
      {
        label: "Sunday airline",
        isValid: fieldIsValid(Field.SUNDAY_FLIGHT_AIRLINE),
      },
      {
        label: "Sunday departure date and time",
        isValid: fieldIsValid(Field.SUNDAY_FLIGHT_DEPARTURE_TIME),
      },
      {
        label: "Rapid contact information",
        isValid: isRapidContactInformationValid,
      },
      {
        label: "Flight payment currency",
        isValid: fieldIsValid(Field.FLIGHT_CURRENCY),
      },
      { label: "Flight cost", isValid: isFlightAmountValid },
      {
        label: "Flight receipt",
        isValid: Boolean(fetchedFile) && !isFileLoading && !fileError,
      },
      {
        label: "Reimbursement method",
        isValid: fieldIsValid(Field.REIMBURSEMENT_METHOD),
      },
      {
        label:
          reimbursementMethod === "paypal"
            ? "PayPal account"
            : "e-Transfer email",
        isValid: isReimbursementRecipientValid,
      },
      {
        label: "Reimbursement conditions acknowledgement",
        isValid: fieldIsValid(Field.REIMBURSEMENT_ACKNOWLEDGEMENT),
      },
      { label: "Visa tracking number", isValid: isVisaTrackingValid },
    ];
  };

  const submit = async () => {
    const response = convertResponseTypes(responsesState);
    if (fetchedFile) {
      response[Field.TRAVEL_RECEIPT] = fetchedFile;
    }

    // Validate ALL fields first
    updateErrors(response);

    // File-specific validation
    if (!fetchedFile) {
      setReceiptError("Please upload your flight receipt.");
    } else {
      setReceiptError("");
    }

    if (
      customValid() &&
      !isFileLoading &&
      !fileError &&
      fetchedFile &&
      isFlightAmountValid &&
      isVisaTrackingValid
    ) {
      try {
        const { errors } = await updateResponses(response);

        if (errors) {
          throw new Error(
            "We weren't able to submit your answers, please try again."
          );
        }

        successToast("Answers successfully saved!");
        navigateNext();
      } catch (e) {
        if (typeof e === "object") {
          errorToast((e as GraphQLError)?.message);
        } else {
          errorToast((e as Error).message);
        }
      }
    } else {
      window.scrollTo(0, 0);
      errorToast(formatValidationIssues(getValidationIssues()));
    }
  };

  const [showFlightModal, setShowFlightModal] = useState(false);

  // Automatically clear the receipt error whenever a file is present
  useEffect(() => {
    if (fetchedFile) {
      setReceiptError("");
    }
  }, [fetchedFile]);

  if (isFlightClosed) {
    return <Text mods="bold h1">This form is now closed.</Text>;
  } else {
    return (
      <>
        <FormContainer>
          <FlightDetails
            showError={showError}
            day="Friday"
            fields={{
              airport: Field.ARRIVAL_AIRPORT,
              flightNo: Field.FRIDAY_FLIGHT_NUMBER,
              airline: Field.FRIDAY_FLIGHT_AIRLINE,
              arrivalTime: Field.FRIDAY_FLIGHT_ARRIVAL_TIME,
            }}
            errors={errors}
            setErrors={setErrors}
            setResponsesState={setResponsesState}
            responsesState={responsesState}
            isReadOnly={isReadOnly}
          />
          <FlightDetails
            showError={showError}
            day="Sunday"
            fields={{
              airport: Field.DEPARTURE_AIRPORT,
              flightNo: Field.SUNDAY_FLIGHT_NUMBER,
              airline: Field.SUNDAY_FLIGHT_AIRLINE,
              arrivalTime: Field.SUNDAY_FLIGHT_DEPARTURE_TIME,
            }}
            errors={errors}
            setErrors={setErrors}
            setResponsesState={setResponsesState}
            responsesState={responsesState}
            isReadOnly={isReadOnly}
          />
          <div>
            <Text mods="bold textBody">Rapid contact information</Text>
            <Spacer height={12} />
            <Text>
              Provide a phone number or email address that we can use to reach
              you reliably about your travel during the event.
            </Text>
            <Spacer height={12} />
            <InputWrapper
              maxLength={200}
              placeholder="Enter a phone number or email address"
              value={responsesState[Field.TRAVEL_PHONE_NUMBER] ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setResponsesState(
                  Field.TRAVEL_PHONE_NUMBER,
                  e.currentTarget.value
                );
              }}
              disabled={isReadOnly}
              size="md"
              error={
                showError &&
                !isRapidContactInformationValid &&
                "Please enter a valid phone number or email address"
              }
            />
            <HelperText>
              We may use this information to send urgent travel updates or
              confirm your travel status.
            </HelperText>
          </div>
          <div>
            <SectionHeader>Flight cost</SectionHeader>
            <Spacer height={24} />
            <ReimbursementNotice>
              {reimbursementAmount > 0 ? (
                <Text>
                  You are flying from <b>{travelLocation}</b> and are approved
                  for travel reimbursement of up to{" "}
                  <b>CAD${reimbursementAmount}</b>. Any amount over this limit
                  will not be reimbursed, even if your flight costs more.
                </Text>
              ) : (
                <Text>
                  We don&apos;t currently have an approved flight reimbursement
                  amount for {travelLocation || "your location"}. If you think
                  this is a mistake, contact{" "}
                  <Link href="mailto:travel@hackthenorth.com">
                    travel@hackthenorth.com
                  </Link>{" "}
                  before booking.
                </Text>
              )}
            </ReimbursementNotice>
            <Spacer height={24} />
            <Row>
              <Label
                value="What currency did you pay for your flight in?"
                caption="This should be the currency shown on your receipts."
                error={
                  didEdit.currency || showError
                    ? errors[Field.FLIGHT_CURRENCY]
                    : null
                }
              >
                <StyledSelect
                  fullWidth
                  value={responsesState[Field.FLIGHT_CURRENCY] as string}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    const validator = VALIDATORS[Field.FLIGHT_CURRENCY];
                    if (validator) {
                      setErrors({
                        ...errors,
                        [Field.FLIGHT_CURRENCY]: validator(e.target.value)
                          ? ""
                          : "Please select a valid currency.",
                      });
                    }
                    setDidEdit({ ...didEdit, currency: true });
                    setResponsesState(Field.FLIGHT_CURRENCY, e.target.value);
                  }}
                >
                  {optionsToNorthV2Options(REIMBURSEMENT_SELECT_OPTIONS)}
                </StyledSelect>
              </Label>
              <Spacer width={50} />
              <Label value="What is the total cost of your flight?">
                <StyledTextInput
                  value={responsesState[Field.FLIGHT_AMOUNT] as number}
                  placeholder="Enter your total cost"
                  hint="Please enter the amount shown on your receipts with taxes."
                  disabled={isReadOnly}
                  error={
                    showError &&
                    !isFlightAmountValid &&
                    "Please enter the total cost of your flight"
                  }
                  onChange={(e) => {
                    const validator = VALIDATORS[Field.FLIGHT_AMOUNT];
                    if (validator) {
                      setErrors({
                        ...errors,
                        [Field.FLIGHT_AMOUNT]: validator(e.target.value)
                          ? ""
                          : "Please enter a valid amount.",
                      });
                    }
                    setDidEdit({ ...didEdit, amount: true });
                    setResponsesState(Field.FLIGHT_AMOUNT, e.target.value);
                  }}
                />
              </Label>
            </Row>
            <Spacer height={36} />
            <Label
              value="Flight receipt upload"
              caption="Please be sure you upload receipts for all your travel purchases with the date and price clearly visible.. Your payments will not be reimbursed without receipts. "
            >
              <Text mods="big textBody">
                The receipts you upload should include the amount paid and
                flight information in one document, as well as the following
                information:
                <ul>
                  <li>
                    Your <b>full name</b> (please note on the receipt if the
                    ticket was purchased by someone else)
                  </li>
                  <li> Proof that the payment was processed</li>
                </ul>
              </Text>
              <ReceiptUpload />
            </Label>
          </div>
          <div>
            <SectionHeader>Reimbursement method</SectionHeader>
            <Spacer height={24} />
            <Reimbursement
              showErrors={showError}
              errors={errors}
              setErrors={setErrors}
              responsesState={responsesState}
              setResponsesState={setResponsesState}
              isReadOnly={isReadOnly}
            />
          </div>
          {responsesState[Field.REQUIRES_VISA] && (
            <div>
              <SectionHeader>Visa tracking information</SectionHeader>
              <Spacer height={24} />
              <Label value="What is your visa tracking number?">
                <StyledTextInput
                  value={responsesState[Field.VISA_TRACKING] as string}
                  placeholder="Enter your tracking number"
                  disabled={isReadOnly}
                  hint={
                    <span>
                      If you require a visa or eTA please enter your tracking
                      number so we can confirm your spot at Hack the North.{" "}
                      <b>
                        Once you get your visa or eTA approved, email{" "}
                        <StyledLink href="mailto:travel@hackthenorth.com">
                          travel@hackthenorth.com
                        </StyledLink>{" "}
                        to let us know
                      </b>
                      .
                    </span>
                  }
                  error={
                    showError &&
                    !isVisaTrackingValid &&
                    "Please enter your visa tracking number."
                  }
                  onChange={(e) => {
                    const validator = VALIDATORS[Field.VISA_TRACKING];
                    if (validator) {
                      setErrors({
                        ...errors,
                        [Field.VISA_TRACKING]: validator(e.target.value)
                          ? ""
                          : "Please enter a visa tracking number.",
                      });
                    }
                    setDidEdit({ ...didEdit, visaTracking: true });
                    setResponsesState(Field.VISA_TRACKING, e.target.value);
                  }}
                />
              </Label>
            </div>
          )}
          <Divider />
          <div>
            <BusComponent
              isBlocking={isBlocking}
              setIsBlocking={setIsBlocking}
              isReadOnly={isReadOnly}
              description={
                <>
                  We recommend reserving a Hack the North bus to and from the
                  airport whenever a route works with your flights. If you
                  don&apos;t need a bus, select <b>“I don&apos;t need a bus”</b>
                  .
                </>
              }
            />
          </div>
          {!isReadOnly && (
            <Row>
              <Button
                color="primary"
                trailingIcon={<Icon name="arrow-right" />}
                size="lg"
                onClick={() => {
                  // Populate all errors for untouched fields
                  updateErrors(responsesState);
                  setShowError(true);
                  if (
                    customValid() &&
                    !isFileLoading &&
                    !fileError &&
                    isRapidContactInformationValid &&
                    fetchedFile
                  ) {
                    setShowFlightModal(true);
                  } else {
                    window.scrollTo(0, 0);

                    if (!fetchedFile) {
                      setReceiptError("Please upload a receipt file");
                    }

                    errorToast(formatValidationIssues(getValidationIssues()));
                  }
                }}
              >
                Submit travel plan
              </Button>
              <TravelModal
                isOpen={showFlightModal}
                onConfirm={submit}
                onCancel={() => setShowFlightModal(false)}
              />
            </Row>
          )}
        </FormContainer>
      </>
    );
  }
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 42px;
`;

export const SectionHeader = styled(Text).attrs({
  mods: "h3 brandSecondary bold",
})`
  margin-bottom: 24px;
`;

const Row = styled.div`
  display: flex;
  width: 100%;
`;

export const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 36px;
`;

const StyledTextInput = styled(TextInput).attrs({ size: "md" })`
  width: 100%;
`;

const StyledSelect = styled(Select).attrs({ size: "lg" })`
  width: 100%;
  color: rgba(31, 41, 55, 1) !important;
`;

const StyledLink = styled(Link)`
  font-size: 12px !important;
`;

const InputWrapper = styled(TextInput).attrs({ size: "md" })`
  width: 100%;
`;

const HelperText = styled.p`
  margin-top: 4px;
  font-size: 12px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const ReimbursementNotice = styled.div`
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
  font-size: 12px;
  margin-top: 4px;
`;
